import { useWorkerProfile } from "hooks/useWorkerProfile";
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { useOrganizationalStructureStore } from "web-apps/company/stores/organizationalStructureStore/organizationalStructureStore";
import { CreateModal } from "./createModal.component";
import { TeamRow } from "./teamRow.component";
import { WorkerProfile } from "./workerProfile.component";
import { EmptyStructure } from "./emptyStructure.component";
import { Text } from "components/Typography/text.styled";
import { FontFamily } from "config/font";
import { ButtonComponent } from "../general/button/button.component";

type Props = {
  unitId: number;
  searchText?: string;
};

export const UnitStructure: React.FC<Props> = (props) => {
  const {
    clickedWorker,
    showWorkerProfile,
    setClickedWorker,
    setShowWorkerProfile,
  } = useWorkerProfile();

  const { t } = useTranslation();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [loadingTeams, setLoadingTeams] = useState<Set<number>>(new Set());

  const [{ company }] = useCompanyStore();
  const [organizationalStructureState, organizationalStructureActions] =
    useOrganizationalStructureStore();

  const pageSize = 10;

  const { openTeams, teamMembers, teamPagination } =
    organizationalStructureState;

  const unit = useMemo(() => {
    return organizationalStructureState.structure.entities
      .flatMap((entity) => entity.units)
      .find((unit) => unit.id === props.unitId);
  }, [organizationalStructureState.structure, props.unitId]);

  const workGroups = useMemo(() => {
    return unit?.workGroups || [];
  }, [unit?.workGroups]);

  const teamMatchesSearch = useCallback(
    (workgroup: any) => {
      if (!props.searchText) return true;

      const lowerSearchText = props.searchText.toLowerCase();
      if (
        workgroup.name &&
        workgroup.name.toLowerCase().includes(lowerSearchText)
      ) {
        return true;
      }

      return false;
    },
    [props.searchText]
  );

  const filteredWorkGroups = useMemo(() => {
    return workGroups.filter(teamMatchesSearch);
  }, [workGroups, teamMatchesSearch]);

  const fetchAllTeamMembers = useCallback(
    async (teamId: number) => {
      if (!company) return;

      setLoadingTeams((prev) => new Set(prev).add(teamId));

      try {
        const firstPageRes =
          await organizationalStructureActions.getTeamMembers(
            company.id,
            teamId,
            0,
            pageSize
          );

        const totalCount = firstPageRes.data.total;

        if (totalCount > pageSize) {
          const allMembersRes =
            await organizationalStructureActions.getTeamMembers(
              company.id,
              teamId,
              0,
              totalCount
            );

          const allMembers = allMembersRes.data.workers;
          const totalPages = Math.ceil(totalCount / pageSize);

          organizationalStructureActions.updateTeamStateMembers(
            teamId,
            allMembers,
            0,
            totalPages
          );
        } else {
          const members = firstPageRes.data.workers;
          const totalPages = Math.ceil(totalCount / pageSize);

          organizationalStructureActions.updateTeamStateMembers(
            teamId,
            members,
            0,
            totalPages
          );
        }
      } catch (err) {
        console.error("Error fetching team members", err);
      } finally {
        setLoadingTeams((prev) => {
          const newSet = new Set(prev);
          newSet.delete(teamId);
          return newSet;
        });
      }
    },
    [company, organizationalStructureActions, pageSize]
  );

  const fetchedTeamsRef = useRef<Set<number>>(new Set());

  const handleTeamToggle = useCallback(
    (workgroup: any) => {
      const teamId = workgroup.id;
      const isCurrentlyOpen = openTeams.includes(teamId);

      if (!isCurrentlyOpen && !fetchedTeamsRef.current.has(teamId)) {
        fetchedTeamsRef.current.add(teamId);
        fetchAllTeamMembers(teamId);
      }

      organizationalStructureActions.toggleTeam(teamId);
    },
    [organizationalStructureActions, openTeams, fetchAllTeamMembers]
  );

  const handlePageChange = useCallback(
    (teamId: number, newPage: number) => {
      organizationalStructureActions.updateTeamPagination(teamId, newPage);
    },
    [organizationalStructureActions]
  );

  const getPaginatedMembers = useCallback(
    (teamId: number, page: number) => {
      const allMembers = teamMembers[teamId] || [];
      const startIndex = page * pageSize;
      const endIndex = startIndex + pageSize;
      return allMembers.slice(startIndex, endIndex);
    },
    [teamMembers, pageSize]
  );

  const getTeamMemberCount = useCallback(
    (unitId: number, teamId: number) => {
      const unit = organizationalStructureState.structure.entities
        .flatMap((entity) => entity.units)
        .find((unit) => unit.id === unitId);

      if (!unit) return 0;

      const workGroup = unit.workGroups.find((wg) => wg.id === teamId);

      if (!workGroup) return 0;

      return workGroup.workers.length;
    },
    [organizationalStructureState.structure]
  );

  useEffect(() => {
    if (organizationalStructureState.members) {
      Object.entries(organizationalStructureState.members).forEach(
        ([teamId, members]) => {
          const teamIdNum = parseInt(teamId);
          if (openTeams.includes(teamIdNum) && members) {
            organizationalStructureActions.updateTeamStateMembers(
              teamIdNum,
              members,
              0,
              Math.ceil(members.length / pageSize)
            );
          }
        }
      );
    }
  }, [organizationalStructureState.members, openTeams, pageSize]);

  if (!unit) return null;

  if (!filteredWorkGroups.length && unit.name !== "Unhandled") {
    if (props.searchText && workGroups.length > 0) {
      return (
        <Text
          fontFamily={FontFamily.MontserratRegular}
          style={{ padding: "20px", textAlign: "center" }}
        >
          {t("OrganizationalStructure.NoSearchResults", {
            searchText: props.searchText,
          })}
        </Text>
      );
    }
    return <EmptyStructure type="Team" id={unit.id} />;
  }

  return (
    <>
      {filteredWorkGroups.map((workgroup: any) => {
        const teamId = workgroup.id;
        const isOpen = openTeams.includes(teamId);
        const allMembers = teamMembers[teamId] || [];
        const pagination = teamPagination[teamId] || {
          currentPage: 0,
          totalPages: Math.ceil(allMembers.length / pageSize),
        };

        const currentPageMembers = getPaginatedMembers(
          teamId,
          pagination.currentPage
        );

        return (
          <TeamRow
            key={teamId}
            workgroup={workgroup}
            isOpen={isOpen}
            members={currentPageMembers}
            allMembers={allMembers}
            pagination={pagination}
            isLoading={loadingTeams.has(teamId)}
            onToggle={() => handleTeamToggle(workgroup)}
            onPageChange={(page) => handlePageChange(teamId, page)}
            onWorkerClick={setClickedWorker}
            searchText={props.searchText}
            memberCount={getTeamMemberCount(props.unitId, workgroup.id)}
          />
        );
      })}

      {!props.searchText && unit.name !== "Unhandled" && (
        <div style={{ margin: "0 auto" }}>
          <ButtonComponent
            onClick={() => setShowCreateModal(true)}
            action={true}
            size={"small"}
            marginLeft={50}
            marginTop={10}
            marginBottom={10}
          >
            {t("OrganizationalStructure.CreateTeam")}
          </ButtonComponent>
        </div>
      )}

      <CreateModal
        setShowCreateModal={setShowCreateModal}
        showCreateModal={showCreateModal}
        type="Team"
        id={unit.id}
      />

      {clickedWorker && (
        <WorkerProfile
          worker={clickedWorker}
          showWorkerProfile={showWorkerProfile}
          setShowWorkerProfile={setShowWorkerProfile}
          setClickedWorker={setClickedWorker}
        />
      )}
    </>
  );
};
